/**
 * v3.15-1154 — Public-page layout & nav.
 *
 * Used by /about, /mission, /faq. Provides a consistent professional nav
 * (Prestigious Hub wordmark + nav links + Partner Login CTA) and reuses
 * the existing <Footer /> at the bottom. No authentication concerns — these
 * pages are 100% static marketing.
 */
import { Link, NavLink } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import Footer from './Footer';
import AskGhostPublic from './AskGhostPublic';   // v3.15-1167

const NAV_LINKS = [
  { to: '/',               label: 'Home'              },
  { to: '/platform',       label: 'Platform'          },
  { to: '/platform-tour',  label: 'Inside the Hub'    },
  { to: '/ghost',          label: 'Ghost'             },
  { to: '/about',          label: 'About'             },
  { to: '/presentation',   label: 'Presentation'      },
];

const EXTERNAL_LINKS = [
  { href: 'https://easyquickfund.com',    label: 'EasyQuickFund'    },
  { href: 'https://creditforward360.com', label: 'CreditForward360' },
];

export const PublicNav = () => (
  <>
    {/* v3.15-1157 — Premium enterprise top bar with gold hairline to restore
        the Prestigious Hub identity (was a thin generic white strip before). */}
    <div className="bg-slate-900 border-b border-gold/30">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-8 flex items-center justify-between text-[11px] tracking-[0.18em] uppercase">
        <span className="text-gold font-medium">Prestigious Companies Network</span>
        <span className="hidden sm:inline text-slate-400">
          Powering EasyQuickFund · Business Funding · CreditForward360
        </span>
      </div>
    </div>
    <header className="bg-white border-b border-slate-200 sticky top-0 z-30 shadow-sm">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex flex-col items-start group" data-testid="public-nav-home">
          <span className="font-heading text-xl font-bold text-primary tracking-tight leading-none">Prestigious Hub</span>
          <span className="mt-1 h-[2px] w-10 bg-gradient-gold rounded-full group-hover:w-14 transition-all" />
        </Link>
        <nav className="hidden md:flex items-center gap-5 lg:gap-6">
          {NAV_LINKS.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === '/'}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${isActive ? 'text-primary border-b-2 border-gold pb-1' : 'text-slate-600 hover:text-primary'}`
              }
              data-testid={`public-nav-${n.label.toLowerCase()}`}
            >
              {n.label}
            </NavLink>
          ))}
          {EXTERNAL_LINKS.map((n) => (
            <a
              key={n.href}
              href={n.href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-slate-600 hover:text-primary transition-colors inline-flex items-center gap-1"
              data-testid={`public-nav-${n.label.toLowerCase()}`}
            >
              {n.label}
              <ExternalLink className="h-3 w-3" />
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-4">
          <Link
            to="/presentation"
            className="text-sm font-medium text-slate-600 hover:text-primary transition-colors"
            data-testid="public-nav-become-partner"
          >
            Become a Partner
          </Link>
          <Link
            to="/login"
            className="bg-primary text-white text-sm font-semibold px-4 py-2 rounded hover:bg-slate-800 transition-colors"
            data-testid="public-nav-login"
          >
            Partner Login
          </Link>
        </div>
      </div>
    </header>
  </>
);

const PublicLayout = ({ children, testId }) => (
  <div className="min-h-screen flex flex-col bg-slate-50" data-testid={testId}>
    <PublicNav />
    <main className="flex-1">{children}</main>
    <Footer />
    {/* v3.15-1167 — Ghost is the intelligence layer, not a nav item. Surface
        it as a persistent floating assistant on every public page. */}
    <AskGhostPublic />
  </div>
);

export default PublicLayout;
